import React, { useRef } from 'react';
import { Controller, type Control, type UseFormWatch } from 'react-hook-form';
import { IconPhoto, IconRefresh } from '@tabler/icons-react';
import { Button } from '@/admin/components/ui/button';
import { Label } from '@/admin/components/ui/label';
import { Switch } from '@/admin/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import type { Skill } from '@/admin/lib/api';
import type { SkillFormData } from '@/admin/pages/skill-detail';

interface SettingsSidebarProps {
  skill: Skill;
  control: Control<SkillFormData>;
  watch: UseFormWatch<SkillFormData>;
  onStatusChange: (status: string) => void;
  uploadingHero: boolean;
  onHeroUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  uploadingVideo: boolean;
  onVideoUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  syncing: boolean;
  onSync: () => Promise<void>;
}

export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  skill,
  control,
  watch,
  onStatusChange,
  uploadingHero,
  onHeroUpload,
  uploadingVideo,
  onVideoUpload,
  syncing,
  onSync,
}) => {
  const heroInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleHeroChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await onHeroUpload(e);
    if (heroInputRef.current) {
      heroInputRef.current.value = '';
    }
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await onVideoUpload(e);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const status = watch('status');

  return (
    <div className="w-64 border-l bg-card flex flex-col shrink-0">
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-5">
          {/* Status Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs px-2"
                onClick={onSync}
                disabled={syncing}
              >
                <IconRefresh
                  className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`}
                />
                Sync
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="status" className="text-xs">
                Visibility
              </Label>
              <Select value={status} onValueChange={onStatusChange}>
                <SelectTrigger id="status" className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-2.5 border rounded-lg bg-muted/20">
              <div className="space-y-0">
                <Label htmlFor="featured" className="text-xs cursor-pointer">
                  Featured
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  Show on homepage
                </p>
              </div>
              <Controller
                name="featured"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="featured"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          <div className="h-px bg-border" />

          {/* Hero Image */}
          <div className="space-y-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Cover Image
            </h3>
            <div
              className={`relative group border rounded-lg overflow-hidden cursor-pointer transition-all ${
                skill.hero_url
                  ? 'hover:ring-2 ring-primary/30'
                  : 'border-dashed border-2 hover:border-primary/50'
              } ${uploadingHero ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => heroInputRef.current?.click()}
            >
              <input
                ref={heroInputRef}
                type="file"
                accept="image/*"
                onChange={handleHeroChange}
                className="hidden"
              />
              {skill.hero_url ? (
                <div className="relative aspect-video">
                  <img
                    src={skill.hero_url}
                    alt="Hero"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-white text-[11px] font-medium flex items-center gap-1.5">
                      <IconPhoto className="h-3.5 w-3.5" />
                      Change
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-video flex flex-col items-center justify-center text-muted-foreground bg-muted/30">
                  <IconPhoto className="h-6 w-6 mb-1.5" />
                  <span className="text-[10px]">
                    {uploadingHero ? 'Uploading...' : 'Add cover image'}
                  </span>
                </div>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Used for og:image and skill page
            </p>
          </div>

          {/* Cover Video */}
          <div className="space-y-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Cover Video
            </h3>
            <div
              className={`relative group border rounded-lg overflow-hidden cursor-pointer transition-all ${
                skill.video_url
                  ? 'hover:ring-2 ring-primary/30'
                  : 'border-dashed border-2 hover:border-primary/50'
              } ${uploadingVideo ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => videoInputRef.current?.click()}
            >
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoChange}
                className="hidden"
              />
              {skill.video_url ? (
                <div className="relative aspect-video">
                  <video
                    src={skill.video_url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-white text-[11px] font-medium flex items-center gap-1.5">
                      <IconPhoto className="h-3.5 w-3.5" />
                      Change
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-video flex flex-col items-center justify-center text-muted-foreground bg-muted/30">
                  <IconPhoto className="h-6 w-6 mb-1.5" />
                  <span className="text-[10px]">
                    {uploadingVideo ? 'Uploading...' : 'Add cover video'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-border" />

          {/* System Info */}
          <div className="space-y-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              System
            </h3>
            <dl className="text-[10px] space-y-1.5 text-muted-foreground">
              <div className="flex justify-between">
                <dt>ID</dt>
                <dd className="font-mono text-foreground/70">
                  {skill.id.slice(0, 8)}…
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>Name</dt>
                <dd className="font-mono text-foreground/70">{skill.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Version</dt>
                <dd className="text-foreground/70">{skill.version || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Category</dt>
                <dd className="text-foreground/70">{skill.category || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Created</dt>
                <dd className="text-foreground/70">
                  {new Date(skill.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>Updated</dt>
                <dd className="text-foreground/70">
                  {new Date(skill.updated_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};
